import { useApiInstance } from '@api-hooks/ApiInstanceContext';
import sessionState from '@atoms/session';
import Button from '@components/Button';
import Checkbox from '@components/Checkbox/Checkbox';
import { useGlobalModal } from '@components/GlobalModal';
import InputGroup from '@components/InputGroup/InputGroup';
import ProgressIcon from '@components/base/ProgressIcon';
import LanguageChangeModal from '@components/ivas/base/LanguageChangeModal';
import PasswordChangeModal from '@components/ivas/login/PasswordChangeModal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useProductContext } from '@hooks/useProductContext';
import { useQueryClient } from '@tanstack/react-query';
import LabradorLogo from 'assets/images/labrador-logo-light.png';
import coverImage from 'assets/images/login-bg.png';
import { AxiosError } from 'axios';
import { getOption } from 'common-api/src/management/options/ManagementOption';
import { loginSession } from 'common-api/src/session/Session';
import { VALIDATION_QUERY_KEY } from 'common-hooks/src/useValidation';
import { PRODUCT, SessionInfo } from 'common-types';
import { MANAGE_OPTION_CATEGORY } from 'common-types/src/card';
import { getSafeRedirectUrl } from 'common-utils';
import { t as i18n } from 'i18next';
import Cookies from 'js-cookie';
import moment from 'moment';
import { GetServerSidePropsContext } from 'next';
import { getServerSession } from 'next-auth';
import { signIn } from 'next-auth/react';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaRegCopyright, FaUser } from 'react-icons/fa6';
import { MdLanguage } from 'react-icons/md';
import { RiLockPasswordFill } from 'react-icons/ri';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { z } from 'zod';
import { E2E_TEST_IDS } from 'common-const';
import { authOptions } from './api/auth/[...nextauth]';

const DEFAULT_AWAY_DAYS_FOR_PASSWORD_CHANGE = 30;

const schema = z.object({
  id: z
    .string({
      required_error: i18n('enterYourId'),
    })
    .trim()
    .min(1, {
      message: i18n('enterYourId'),
    }),
  password: z
    .string()
    .trim()
    .min(1, {
      message: i18n('enterYourPassword'),
    }),
});

type Schema = z.infer<typeof schema>;

/**
 * 로그인 페이지
 * 로그인 성공 시, 비밀번호 변경 주기 확인
 * 비밀번호 변경 주기가 지나지 않은 경우, 로그인 성공 후 메인 페이지로 이동
 * 비밀번호 변경 주기가 지난 경우, 비밀번호 변경 모달 노출(로그인 성공 결과값을 보관)
 * 비밀번호 변경 후, 보관한 로그인 성공 결과값을 사용하여 로그인 후 메인 페이지로 이동
 */
export default function Login() {
  const router = useRouter();
  const apiInstance = useApiInstance();

  const {
    setCurrentProduct,
    strings: { title: productTitle },
  } = useProductContext();

  const queryClient = useQueryClient();
  const { resetErrorModal, resetAccessDeniedModal } = useGlobalModal();
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const setUserState = useSetRecoilState(sessionState);
  const resetUserState = useResetRecoilState(sessionState);
  const [temporaryLoginResult, setTemporaryLoginResult] = useState<SessionInfo | null>(null);

  const [idRemember, setIdRemember] = useState(false);

  const { t } = useTranslation();
  const title = t('login');

  const [showLoginErrorMessage, setShowLoginErrorMessage] = useState(false);
  const [errMsg, setErrMsg] = useState<string | ''>('');

  const {
    handleSubmit,
    register,
    watch,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<Schema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    resetErrorModal();
    resetAccessDeniedModal();
    queryClient.invalidateQueries({ queryKey: [VALIDATION_QUERY_KEY] });

    // set AccountId from localStorage key('ivas.rememberedAccountId') if exists
    const rememberedAccountId = localStorage.getItem('ivas.rememberedAccountId');
    if (rememberedAccountId) {
      setIdRemember(true);
      setValue('id', rememberedAccountId, { shouldValidate: true, shouldDirty: true });
    }

    const { redirect } = router.query;
    const safeRedirectUrl = getSafeRedirectUrl(redirect as string);
    const firstPathFromRedirect = safeRedirectUrl.split('/')[1] ?? '';
    switch (firstPathFromRedirect) {
      case 'oss':
        setCurrentProduct(PRODUCT.ENTRY);
        break;
      case 'server':
        setCurrentProduct(PRODUCT.SERVERCARE);
        break;
      default:
        setCurrentProduct(null);
        break;
    }
  }, []);

  // 비밀번호 변경 후 next-auth 세션 업데이트 및 Redirect
  async function logIn(result?: SessionInfo | null) {
    const res = await signIn('bypass', {
      ...result,
      redirect: false,
    });
    queryClient.clear();
    await queryClient.invalidateQueries();
    await queryClient.invalidateQueries({ queryKey: ['getSessionInfo'] });
    await queryClient.invalidateQueries({ queryKey: ['getGroupList'] });
    await queryClient.invalidateQueries({ queryKey: [VALIDATION_QUERY_KEY] });
    resetUserState();
    resetErrorModal();
    if (result) {
      Cookies.set('accessToken', result?.token, { path: '/' });
      setUserState(result);
    }
    if (res?.ok && res.status === 200) {
      // get 'redirect' from query string
      const { redirect } = router.query;
      const redirectTo = getSafeRedirectUrl(redirect as string);
      router.replace(redirectTo);
    } else {
      setShowLoginErrorMessage(true);
      setError('id', { message: '' });
      setError('password', { message: '' });
    }
  }

  async function onSubmit(data: Schema) {
    setIsLoading(true);
    try {
      // 세션 업데이트 전, 유저 정보 획득 시도
      const loginResult = await loginSession(apiInstance, {
        userId: data?.id ?? '',
        password: data?.password ?? '',
      });

      // 로그인 시도한 유저의 비밀번호 변경 주기 확인
      const passwordPolicy = await getOption(apiInstance, MANAGE_OPTION_CATEGORY.PASSWORD_POLICY, {
        'X-API-KEY': loginResult.token,
      });

      const now = moment();
      const lastPasswordChange = moment(loginResult?.lastPasswordChangedAt);
      // 비밀번호 변경 주기가 지났는지 확인
      const isRetentionPeriodAway =
        now.diff(lastPasswordChange, 'days') >=
        Number(passwordPolicy?.retentionPeriod ?? DEFAULT_AWAY_DAYS_FOR_PASSWORD_CHANGE);

      if (idRemember) {
        localStorage.setItem('ivas.rememberedAccountId', data.id);
      }
      // 비밀번호 변경 주기가 지나서 비밀번호 변경이 필요한 경우
      if (isRetentionPeriodAway) {
        setShowPasswordChangeModal(true);
        setTemporaryLoginResult(loginResult);
        return;
      }

      // 비밀번호 변경 주기가 지나지 않아서 바로 로그인
      await logIn(loginResult);
    } catch (e: any) {
      const error = e as AxiosError;
      setErrMsg(error.translatedErrorMessage ?? t('unExpectedErrorMsg'));
      setShowLoginErrorMessage(true);
      setError('id', { message: '' });
      setError('password', { message: '' });
    } finally {
      setIsLoading(false);
    }
  }

  // clear error message when react-hook-form value changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      setShowLoginErrorMessage(false);
      setErrMsg('');
      if (!name) return;
      if (errors[name]) clearErrors(name);
    });
    return () => subscription.unsubscribe();
  }, [clearErrors, errors, setShowLoginErrorMessage, watch]);

  return (
    <main>
      <Head>
        <title>{title}</title>
        <meta name="description" content="IVAS" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <PasswordChangeModal
        redirectUrl="/"
        open={showPasswordChangeModal}
        loginResult={temporaryLoginResult}
        signIn={(res) => logIn(res)}
        setOpen={(open) => setShowPasswordChangeModal(open)}
      />

      <LanguageChangeModal
        open={showLanguageModal}
        setOpen={(value) => setShowLanguageModal(value)}
      />

      <section className="flex h-[calc(100vh-var(--header-height,50px))] gap-32px min-h-[750px] w-screen min-w-0 md:min-w-[540px] flex-col items-center justify-center">
        <h1 className="w-full font-bold text-center opacity-100 text-ivas-gray-70 text-40px">
          {productTitle}
        </h1>
        <Image
          src={coverImage}
          className="relative z-[-1]"
          layout="fill"
          objectFit="cover"
          placeholder="blur"
        />
        <div className="flex relative flex-col justify-center items-center space-y-1 bg-white rounded shadow-2xl drop-shadow-2xl">
          <div className="flex flex-col justify-center items-center px-14 py-36px gap-16px">
            <div className="flex py-3 w-130px mb-20px">
              <Image src={LabradorLogo} alt="Labrador Logo Image" placeholder="blur" />
            </div>
            <form
              id="login-form"
              data-testid={E2E_TEST_IDS.login.form}
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col w-[320px] h-[160px]"
            >
              <div className="space-y-6px">
                <InputGroup
                  inputClassName="ivas-login-input"
                  {...register('id')}
                  id="id"
                  data-testid={E2E_TEST_IDS.login.inputId}
                  size="lg"
                  error={errors?.id}
                  startIcon={
                    <div className="w-18px">
                      <FaUser className="text-ivas-gray-date" />
                    </div>
                  }
                  placeholder={t('placeholderId')}
                  autoComplete="username"
                />
                <InputGroup
                  inputClassName="ivas-login-input"
                  {...register('password')}
                  type="password"
                  id="pw"
                  data-testid={E2E_TEST_IDS.login.inputPassword}
                  size="lg"
                  error={errors?.password}
                  startIcon={
                    <div className="w-18px">
                      <RiLockPasswordFill className="text-ivas-gray-date" />
                    </div>
                  }
                  placeholder={t('placeholderPassword')}
                  autoComplete="current-password"
                />
                {/* <Checkbox checked /> */}
                <label htmlFor="rememberAccountId" className="flex gap-1 items-center">
                  <Checkbox
                    id="rememberAccountId"
                    className="text-ivas-gray-soft !w-14px !h-14px !p-6px"
                    checked={idRemember}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { checked } = e.currentTarget;
                      setIdRemember(checked);
                      if (!checked) {
                        localStorage.removeItem('ivas.rememberedAccountId');
                      }
                    }}
                  />
                  <span className="text-ivas-gray-soft text-body3 leading-14px">
                    {t('rememberAccountId')}
                  </span>
                </label>
              </div>
              {showLoginErrorMessage && (
                <p className="mt-2 text-ivas-invalid text-body3">{errMsg || t('loginErrMsg')}</p>
              )}
              <input type="submit" hidden />
            </form>

            <Button
              color="active"
              size="lg"
              type="submit"
              form="login-form"
              data-testid={E2E_TEST_IDS.login.buttonSubmit}
              className="w-full h-50px"
              disabled={isLoading}
            >
              <div className="flex flex-row justify-center items-center space-x-4">
                <div>{t('login')}</div>
                {isLoading ? <ProgressIcon size={5} /> : null}
              </div>
            </Button>
          </div>

          <div className="flex justify-between items-center px-3 pb-3 w-full">
            <div className="w-1/5 text-ivas-labradorlabs/30">{/* 2024.04.09 #1 */}</div>
            <span className="flex gap-1 justify-center items-center w-3/5 text-ivas-gray-soft text-caption2">
              <FaRegCopyright />
              <span>LABRADOR LABS Inc. All Rights Reserved.</span>
            </span>
            <div className="flex justify-end w-1/5">
              <button
                type="button"
                className="flex justify-end p-1 rounded w-fit hover:bg-white/30"
                tabIndex={-1}
                onClick={() => setShowLanguageModal(true)}
              >
                <MdLanguage className="text-xl text-white/80" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const session = await getServerSession(context.req, context.res, authOptions);

  // next-auth 인증이 되어있으면 mainHref로 이동
  if (session) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  return {
    props: {
      user: null,
      query: context.query,
    },
  };
}
